import { useCallback, useEffect, useState, useMemo } from 'react';
import { useDashboard } from '../../../context/dashboardContext';
import QuoteTable from './components/quoteTable';
import { LoadingModal } from '../../../components/shareable/loadingModal';
import CustomerPagination from '../../../components/shareable/customerPagination';
import { useSearch } from '../../../context/SearchContext';
import PdfModal from './components/PdfModal';
import PDFModal from '../newQuote/component/PDFModal';
import toast from 'react-hot-toast';

const Quotes = () => {
  const { getQuotes, getQuotePdf, deleteQuote, getQuoteByNumber, getAllCustomers, getAllSaws } = useDashboard();
  const { searchQuery, updateSearch } = useSearch();
  const [allQuotes, setAllQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [selectedQuoteNumber, setSelectedQuoteNumber] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Send-Quote modal state: re-uses the email PDFModal from the new-quote flow.
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendModalLoading, setSendModalLoading] = useState(false);
  const [sendData, setSendData] = useState({ pdfUrl: '', quoteNumber: '', customerData: null, sawDetails: null, additionalEmails: [] });
  const itemsPerPage = 10;

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'quotes');
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Helper function to check if a quote is the latest version
  const isLatestVersion = useCallback((quote, allQuotesList) => {
    const parts = quote.quote_number?.split('.');
    if (!parts || parts.length !== 3) return false;
    
    const baseNumber = `${parts[0]}.${parts[1]}`;
    const minorVersion = parseInt(parts[2]);
    
    // Find all quotes with the same base number
    const sameBaseQuotes = allQuotesList.filter(q => {
      const qParts = q.quote_number?.split('.');
      if (!qParts || qParts.length !== 3) return false;
      return `${qParts[0]}.${qParts[1]}` === baseNumber;
    });
    
    if (sameBaseQuotes.length === 0) return false;
    
    // Check if this quote has the highest minor version
    const maxMinorVersion = Math.max(
      ...sameBaseQuotes.map(q => {
        const qParts = q.quote_number?.split('.');
        return parseInt(qParts[2]) || 0;
      })
    );
    
    return minorVersion === maxMinorVersion;
  }, []);

  // Filter quotes based on search query
  const filterQuotes = useCallback((quotes, query) => {
    if (!query.trim()) return quotes;
    
    const lowerCaseQuery = query.toLowerCase().trim();
    
    return quotes.filter(quote => {
      const quoteNumber = (quote.quote_number || '').toLowerCase();
      const customerName = (quote.customer || '').toLowerCase();
      const customerRfq = (quote.customer_rfq_number || '').toLowerCase();

      return (
        quoteNumber.includes(lowerCaseQuery) ||
        customerName.includes(lowerCaseQuery) ||
        customerRfq.includes(lowerCaseQuery)
      );
    });
  }, []);

  // Sort quotes based on sort configuration
  const sortQuotes = useCallback((quotes, sortKey, direction) => {
    const quotesCopy = [...quotes];
    
    if (!sortKey) {
      // Default: sort by creation date descending (newest first). The date lives on
      // the `date` field; `created_at` is a legacy fallback the schema never writes.
      return quotesCopy.sort((a, b) => {
        const dateA = new Date(a.date || a.created_at || 0).getTime();
        const dateB = new Date(b.date || b.created_at || 0).getTime();
        return dateB - dateA;
      });
    }
    
    return quotesCopy.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortKey) {
        case 'customer':
          aValue = (a.customer || '').toLowerCase();
          bValue = (b.customer || '').toLowerCase();
          break;
        case 'customer_rfq_number':
          aValue = (a.customer_rfq_number || '').toLowerCase();
          bValue = (b.customer_rfq_number || '').toLowerCase();
          break;
        case 'quote_number':
          const aParts = (a.quote_number || '').split('.');
          const bParts = (b.quote_number || '').split('.');
          
          const aBase = aParts.length >= 2 ? `${aParts[0]}.${aParts[1]}` : a.quote_number || '';
          const bBase = bParts.length >= 2 ? `${bParts[0]}.${bParts[1]}` : b.quote_number || '';
          
          if (aBase !== bBase) {
            aValue = aBase;
            bValue = bBase;
          } else {
            aValue = aParts.length >= 3 ? parseInt(aParts[2]) || 0 : 0;
            bValue = bParts.length >= 3 ? parseInt(bParts[2]) || 0 : 0;
          }
          break;
        case 'quote_total':
          aValue = parseFloat(a.grand_total || 0);
          bValue = parseFloat(b.grand_total || 0);
          break;
        case 'date':
          // The quote's creation date is stored on the `date` field (set to
          // `new Date()` by the backend on create); `created_at` is a legacy
          // fallback only — the schema has never written it.
          aValue = new Date(a.date || a.created_at || 0).getTime();
          bValue = new Date(b.date || b.created_at || 0).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, []);

  // Process quotes: filter and sort
  const processedQuotes = useMemo(() => {
    let result = [...allQuotes];
    
    const uniqueMap = new Map();
    result.forEach(quote => {
      if (quote._id && !uniqueMap.has(quote._id)) {
        uniqueMap.set(quote._id, quote);
      }
    });
    result = Array.from(uniqueMap.values());
    
    // Then apply search filter
    result = filterQuotes(result, searchQuery);
    
    // Finally apply sorting
    result = sortQuotes(result, sortConfig.key, sortConfig.direction);
    
    return result;
  }, [allQuotes, searchQuery, sortConfig, filterQuotes, sortQuotes]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [processedQuotes.length, searchQuery, sortConfig]);

  // Handle column sorting
  const handleSort = useCallback((key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') { direction = 'desc'; }
    
    setSortConfig({ key, direction });
  }, [sortConfig]);

  // Fetch quotes
  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getQuotes({ page: 1, limit: 1000, sort: 'desc' });

      if (res.success && res.data.results) {
        // Remove duplicates based on _id to prevent duplicate entries
        const uniqueQuotesMap = new Map();
        res.data.results.forEach(quote => {
          if (quote._id && !uniqueQuotesMap.has(quote._id)) { uniqueQuotesMap.set(quote._id, quote); }
        });
        
        // Convert map to array and sort by creation date descending (newest first).
        // Uses the `date` field; `created_at` is a legacy fallback only.
        const uniqueQuotes = Array.from(uniqueQuotesMap.values());
        const sortedQuotes = uniqueQuotes.sort((a, b) => {
          const dateA = new Date(a.date || a.created_at || 0).getTime();
          const dateB = new Date(b.date || b.created_at || 0).getTime();
          return dateB - dateA;
        });
        setAllQuotes(sortedQuotes);
      } else {
        console.error('Failed to fetch quotes:', res.error);
        toast.error(`Failed to fetch quotes: ${res.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error(`Error fetching quotes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [getQuotes]);

  // Handle delete quote
  const handleDeleteQuote = useCallback(async (quoteNumber) => {
    try {
      const result = await deleteQuote(quoteNumber);
      if (result.success) {
        toast.success(`Quote ${quoteNumber} deleted successfully`);
        // Refresh the quotes list after successful deletion
        await fetchQuotes();
        return true;
      } else {
        toast.error(`Failed to delete quote: ${result.error || 'Unknown error'}`);
        return false;
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error(`Error deleting quote: ${error.message}`);
      return false;
    }
  }, [deleteQuote, fetchQuotes]);

  // Initial fetch
  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Calculate pagination
  const totalItems = processedQuotes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = indexOfFirstItem + itemsPerPage;
  const currentQuotes = processedQuotes.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = useCallback((page) => {
    const pageNumber = Number(page);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  }, [totalPages]);

  const handleSendQuote = useCallback(async (quote) => {
    if (!quote?.quote_number) return;
    setSendModalLoading(true);
    setSendData({ pdfUrl: '', quoteNumber: quote.quote_number, customerData: null, sawDetails: null, additionalEmails: [] });
    setSendModalOpen(true);

    try {
      const [pdfRes, quoteRes, customersRes, sawsRes] = await Promise.all([
        getQuotePdf(quote.quote_number, true),
        getQuoteByNumber(quote.quote_number),
        getAllCustomers(),
        getAllSaws(),
      ]);

      if (!pdfRes.success) {
        toast.error(`Failed to load PDF: ${pdfRes.error || 'Unknown error'}`);
        setSendModalOpen(false);
        return;
      }

      const fullQuote = quoteRes?.success ? quoteRes.data : null;
      const customers = customersRes?.success ? customersRes.data : [];
      const saws = sawsRes?.success ? sawsRes.data : [];

      // Match the customer record by name — same pattern as update page.
      const customerRecord = customers.find(c =>
        c.customer === quote.customer ||
        (c.customerData && c.customerData.customer === quote.customer)
      );
      const customerData = customerRecord ? {
        _id: customerRecord._id,
        customer: customerRecord.customer || customerRecord.customerData?.customer,
        title: customerRecord.title || customerRecord.customerData?.title,
        phone: customerRecord.phone || customerRecord.customerData?.phone,
        email: customerRecord.email || customerRecord.customerData?.email,
      } : { customer: quote.customer || '', email: '', title: '' };

      const additionalEmails = (customerRecord?.additional_locations || [])
        .map(loc => (loc?.email || '').trim())
        .filter(Boolean);

      const sawRecord = fullQuote?.saw_id_1039
        ? saws.find(s => s._id === fullQuote.saw_id_1039)
        : null;

      setSendData({
        pdfUrl: pdfRes.url,
        quoteNumber: quote.quote_number,
        customerData,
        sawDetails: sawRecord || null,
        brochure: fullQuote?.brochure || '',
        additionalEmails,
      });
    } catch (err) {
      console.error('handleSendQuote error:', err);
      toast.error(`Failed to prepare send: ${err.message}`);
      setSendModalOpen(false);
    } finally {
      setSendModalLoading(false);
    }
  }, [getQuotePdf, getQuoteByNumber, getAllCustomers, getAllSaws]);

  const handleViewPdf = useCallback(async (quoteNumber) => {
    setSelectedQuoteNumber(quoteNumber);
    setPdfUrl(null);
    setIsPdfModalOpen(true);
    
    try {
      const result = await getQuotePdf(quoteNumber, true);
      if (result.success) {
        setPdfUrl(result.url);
      } else {
        setPdfUrl('');
        console.error('Failed to get PDF:', result.error);
        toast.error(`Failed to load PDF for quote ${quoteNumber}: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setPdfUrl('');
      toast.error(`Error loading PDF: ${error.message}`);
    }
  }, [getQuotePdf]);

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-4">
      <div className="relative mb-4 mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Quotes {searchQuery && `(Filtered: ${totalItems} found)`}
        </h2>
      </div>
      
      <QuoteTable quotes={currentQuotes} currentPage={currentPage} refreshData={fetchQuotes} indexOfFirstItem={indexOfFirstItem} onViewPdf={handleViewPdf} onSendQuote={handleSendQuote} sortConfig={sortConfig} onSort={handleSort} allQuotes={allQuotes} isLatestVersion={isLatestVersion} onDeleteQuote={handleDeleteQuote} />

      <div className="mt-4">
        <CustomerPagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} itemsPerPage={itemsPerPage} handlePageChange={handlePageChange} />
      </div>
      
      <PdfModal isOpen={isPdfModalOpen} onClose={() => setIsPdfModalOpen(false)} pdfUrl={pdfUrl} quoteNumber={selectedQuoteNumber} />

      {sendModalOpen && (
        sendModalLoading || !sendData.pdfUrl ? (
          <LoadingModal loading={true} />
        ) : (
          <PDFModal
            pdfUrl={sendData.pdfUrl}
            quoteNumber={sendData.quoteNumber}
            customerData={sendData.customerData}
            sawDetails={sendData.sawDetails}
            brochure={sendData.brochure}
            selectedAdditionalEmails={sendData.additionalEmails}
            quoteData={null}
            onClose={() => setSendModalOpen(false)}
          />
        )
      )}
    </div>
  );
};

export default Quotes;

